import { formatMoney } from '@loot/shared';
import { useTranslation } from 'react-i18next';
import type { PurchaseStatus, PurchaseSummary } from '../../api.ts';
import type { ProcessGroup } from '../../components/ListFilters.tsx';
import { Badge } from '../../components/ui.tsx';

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';

/** Cores das situações (guia visual): andamento em azul, esperando alguém em bronze. */
const STATUS_TONE: Record<PurchaseStatus, Tone> = {
  awaiting_need_approval: 'accent',
  new: 'info',
  in_review: 'info',
  awaiting_info: 'accent',
  quoting: 'info',
  awaiting_approval: 'accent',
  approved: 'ok',
  rejected: 'off',
  ordered: 'info',
  partially_delivered: 'info',
  completed: 'ok',
  cancelled: 'neutral',
};

/** Grupo de cada situação no filtro das listas (Em aberto, Concluídos, Cancelados e recusados). */
export function purchaseGroup(status: PurchaseStatus): ProcessGroup {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled' || status === 'rejected') return 'cancelled';
  return 'open';
}

const PRIORITY_TONE: Record<PurchaseSummary['priority'], Tone> = {
  low: 'neutral',
  normal: 'neutral',
  high: 'accent',
  urgent: 'off',
};

export function StatusBadge({ status }: { status: PurchaseStatus }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`purchasing.statuses.${status}`)}</Badge>;
}

export function PriorityBadge({ priority }: { priority: PurchaseSummary['priority'] }) {
  const { t } = useTranslation();
  return <Badge tone={PRIORITY_TONE[priority]}>{t(`purchasing.priorities.${priority}`)}</Badge>;
}

/** Datas, horas e valores no idioma da pessoa. */
export function useFormat() {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  // Data sem hora (ex.: "precisa até"): mostrada como está, sem conversão de fuso. No formato do
  // guia visual: 07/10/2026 em português, Oct 7, 2026 em inglês.
  const day = new Intl.DateTimeFormat(locale, {
    ...(locale.startsWith('pt')
      ? ({ day: '2-digit', month: '2-digit', year: 'numeric' } as const)
      : ({ dateStyle: 'medium' } as const)),
    timeZone: 'UTC',
  });
  const moment = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' });
  const monthYear = new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return {
    date: (value: string | null) => (value ? day.format(new Date(`${value}T00:00:00Z`)) : '—'),
    /** Mês de competência ("2026-10" ou "2026-10-01"): "out. de 2026". */
    month: (value: string) => monthYear.format(new Date(`${value.slice(0, 7)}-01T00:00:00Z`)),
    dateTime: (value: string) => moment.format(new Date(value)),
    money: (amount: string | null, currency: string | null) =>
      amount && currency ? formatMoney({ amount, currency }, locale) : '—',
    /** Tamanho de arquivo: "820 KB", "3,4 MB". */
    bytes: (n: number) => {
      const unit = n < 1024 * 1024 ? 'kilobyte' : 'megabyte';
      const value = unit === 'kilobyte' ? Math.max(1, n / 1024) : n / (1024 * 1024);
      return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: unit === 'kilobyte' ? 0 : 1,
      }).format(value);
    },
  };
}
